// /bookings/:id — responsive booking detail with a compact lead summary
// (service requested, addresses, quotes, jobs), embedded communication history,
// and a link into the full lead record.
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaEdit, FaTrash, FaSms,
  FaExternalLinkAlt, FaFileInvoiceDollar, FaBriefcase, FaSpinner, FaBell, FaCheck, FaTimes, FaTools,
} from 'react-icons/fa';
import DashboardLayout from '../../../layouts/DashboardLayout';
import Modal from '../../../components/shared/Modal';
import ModalNotification from '../../../components/shared/ModalNotification';
import CommunicationHistory from '../../../components/communications/CommunicationHistory';
import CallModal from '../../../components/shared/CallModal';
import formatPhone from '../../../components/shared/formatPhone';
import { useAuth } from '../../auth/AuthContext';
import SmsModal from '../../leads/SmsModal';
import { getLeadWithRelated, getLeadEstimates, getLeadJobs } from '../../leads/leadService';
import StatusBadge from '../components/StatusBadge';
import BookingModal from '../components/BookingModal';
import AddToRouteButton from '../../routes/components/AddToRouteButton';
import { getCall, setCallStatus, deleteCall } from '../callScheduleService';
import { formatEastern, formatEasternTime, STATUS_OPTIONS } from '../utils/scheduleUtils';

const Page = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  margin-bottom: ${p => p.theme.spacing(2)};
`;

const BackBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px; background: transparent;
  border: 1px solid ${p => p.theme.colors.inputGray}44; border-radius: 999px;
  color: ${p => p.theme.colors.textSecondary}; padding: 8px 14px; cursor: pointer; font-size: 0.84rem;
  &:hover { color: ${p => p.theme.colors.primary}; border-color: ${p => p.theme.colors.primary}; }
`;

const Grid = styled.div`
  display: grid; grid-template-columns: 1.7fr 1fr; gap: ${p => p.theme.spacing(2.5)};
  align-items: start;
  @media (max-width: 880px) { grid-template-columns: 1fr; }
`;

const Col = styled.div`
  min-width: 0;
  display: flex; flex-direction: column; gap: ${p => p.theme.spacing(2.5)};
`;

const Card = styled.div`
  background: ${p => p.theme.colors.surface}; border-radius: 16px;
  border: 1px solid ${p => p.theme.colors.inputGray}22;
  padding: ${p => p.theme.spacing(2.5)};
  @media (max-width: 480px) { padding: ${p => p.theme.spacing(2)}; }
  h3 { color: ${p => p.theme.colors.primary}; margin: 0 0 ${p => p.theme.spacing(2)} 0; font-size: 1rem; display: flex; align-items: center; gap: 8px; }
`;

const HeroCard = styled(Card)`
  border-left: 4px solid ${p => p.theme.colors.primary};
`;

const HeaderRow = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
  flex-wrap: wrap; margin-bottom: ${p => p.theme.spacing(2)};
  .who { font-size: 1.35rem; font-weight: 700; color: ${p => p.theme.colors.textPrimary}; word-break: break-word; }
  .when { color: ${p => p.theme.colors.textSecondary}; margin-top: 5px; font-size: 0.92rem; }
`;

const Actions = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap; align-items: stretch;
`;

const Btn = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  min-height: 42px; padding: 0 15px; border-radius: 11px;
  border: 1px solid ${p => (p.$danger ? p.theme.colors.error : p.theme.colors.inputGray + '44')};
  background: ${p => (p.$primary ? p.theme.colors.primary : 'transparent')};
  color: ${p => (p.$primary ? '#fff' : p.$danger ? p.theme.colors.error : p.theme.colors.textSecondary)};
  font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: all .15s ease;
  flex: 1 1 auto;
  @media (min-width: 600px) { flex: 0 0 auto; }
  &:hover { opacity: 0.92; border-color: ${p => (p.$danger ? p.theme.colors.error : p.theme.colors.primary)}; }
`;

const StatusSelect = styled.select`
  min-height: 42px; padding: 0 12px; border-radius: 11px; cursor: pointer; flex: 1 1 auto;
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.inputGray}44; font-size: 0.85rem; font-weight: 600;
  @media (min-width: 600px) { flex: 0 0 auto; }
`;

const InfoList = styled.div`
  display: flex; flex-direction: column; gap: 11px;
  .row { display: flex; align-items: center; gap: 11px; color: ${p => p.theme.colors.textPrimary}; font-size: 0.92rem; word-break: break-word; }
  .row svg { color: ${p => p.theme.colors.primary}; flex-shrink: 0; }
  .muted { color: ${p => p.theme.colors.textSecondary}; }
`;

const Stats = styled.div`
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-top: ${p => p.theme.spacing(2)};
`;

const Stat = styled.div`
  background: ${p => p.theme.colors.background}; border-radius: 12px; padding: 14px 12px; text-align: center;
  .num { font-size: 1.5rem; font-weight: 800; color: ${p => p.theme.colors.primary}; }
  .lbl { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.5px; color: ${p => p.theme.colors.textSecondary}; margin-top: 3px; display: flex; align-items: center; gap: 5px; justify-content: center; }
`;

const OpenLeadBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px; width: 100%; justify-content: center;
  margin-top: ${p => p.theme.spacing(2)}; min-height: 46px; border: none; border-radius: 12px;
  background: ${p => p.theme.colors.primary}; color: #fff; font-weight: 700; font-size: 0.92rem; cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const ServiceItem = styled.div`
  background: ${p => p.theme.colors.background}; border-radius: 12px; padding: 14px;
  & + & { margin-top: 10px; }
  .top { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
  .svc { font-weight: 700; color: ${p => p.theme.colors.textPrimary}; font-size: 0.95rem; }
  .info { color: ${p => p.theme.colors.textSecondary}; font-size: 0.86rem; margin-top: 8px; white-space: pre-wrap; line-height: 1.5; }
`;

const LeadPill = styled.span`
  font-size: 0.7rem; font-weight: 700; padding: 3px 10px; border-radius: 10px;
  background: ${p => p.theme.colors.primary}22; color: ${p => p.theme.colors.primary}; white-space: nowrap;
`;

const NotesBox = styled.div`
  margin-top: 10px; padding: 10px 12px; border-radius: 8px;
  background: ${p => p.theme.colors.primary}12; border-left: 3px solid ${p => p.theme.colors.primary};
  font-size: 0.84rem; color: ${p => p.theme.colors.textPrimary}; white-space: pre-wrap; line-height: 1.5;
  .k { display: block; font-size: 0.66rem; letter-spacing: 0.08em; text-transform: uppercase; color: ${p => p.theme.colors.primary}; margin-bottom: 4px; font-weight: 700; }
`;

const MetaRow = styled.div`
  display: flex; justify-content: space-between; gap: 10px; padding: 9px 0;
  border-bottom: 1px solid ${p => p.theme.colors.inputGray}1f; font-size: 0.86rem;
  &:last-child { border-bottom: none; }
  .k { color: ${p => p.theme.colors.textSecondary}; }
  .v { color: ${p => p.theme.colors.textPrimary}; display: flex; align-items: center; gap: 6px; text-align: right; }
`;

const Unlinked = styled.div`
  color: ${p => p.theme.colors.textSecondary}; font-size: 0.9rem; line-height: 1.55;
`;

const Loader = styled.div`
  display: flex; justify-content: center; padding: 100px; color: ${p => p.theme.colors.primary};
  svg { font-size: 32px; animation: spin 1s linear infinite; }
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

const BookingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [call, setCall] = useState(null);
  const [lead, setLead] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);

  const notify = (message, success = true) => setFeedback({ show: true, message, success });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCall(id);
      const c = res.call;
      setCall(c);

      if (c?.lead_id) {
        const [leadRes, estRes, jobsRes] = await Promise.all([
          getLeadWithRelated(c.lead_id).catch(() => null),
          getLeadEstimates(c.lead_id).catch(() => ({ data: [] })),
          getLeadJobs(c.lead_id).catch(() => ({ data: [] })),
        ]);
        setLead(leadRes?.data || null);
        setEstimates(estRes?.data || []);
        setJobs(jobsRes?.data || []);
      } else {
        setLead(null); setEstimates([]); setJobs([]);
      }
    } catch (err) {
      notify('Failed to load booking', false);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    if (status === call.status) return;
    try {
      await setCallStatus(call.id, status);
      notify('Status updated');
      load();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to update status', false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteCall(call.id);
      setDeleteOpen(false);
      navigate('/bookings');
    } catch (err) {
      setDeleteOpen(false);
      notify(err.response?.data?.message || 'Failed to delete', false);
    }
  };

  const openFullLead = () => {
    if (call?.lead_id) window.open(`/leads/view/${call.lead_id}`, '_blank', 'noopener');
  };

  if (loading) {
    return <DashboardLayout><Loader><FaSpinner /></Loader></DashboardLayout>;
  }

  if (!call) {
    return (
      <DashboardLayout>
        <Page>
          <TopBar><BackBtn onClick={() => navigate('/bookings')}><FaArrowLeft /> Back</BackBtn></TopBar>
          <Card><p style={{ color: '#ccc' }}>Booking not found.</p></Card>
        </Page>
      </DashboardLayout>
    );
  }

  const contactName = call.name || lead?.name || '—';
  const contactPhone = call.phone || lead?.phone || '';
  const contactEmail = call.email || lead?.email || '';
  const primaryAddress = lead
    ? [lead.street, lead.city, lead.state, lead.zipcode].filter(Boolean).join(', ')
    : '';
  const serviceRequests = lead?.serviceRequests || [];

  return (
    <DashboardLayout>
      <Page>
        <TopBar>
          <BackBtn onClick={() => navigate('/bookings')}><FaArrowLeft /> Back to bookings</BackBtn>
        </TopBar>

        <HeroCard>
          <HeaderRow>
            <div>
              <div className="who">{contactName}</div>
              <div className="when">{formatEastern(call.slot_start)} – {formatEasternTime(call.slot_end)} ET</div>
            </div>
            <StatusBadge status={call.status} />
          </HeaderRow>

          <Actions>
            <StatusSelect value={call.status} onChange={(e) => changeStatus(e.target.value)} title="Change status">
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </StatusSelect>
            <Btn onClick={() => setEditOpen(true)}><FaEdit /> Reschedule</Btn>
            {contactPhone && <Btn $primary onClick={() => setCallOpen(true)}><FaPhoneAlt /> Call</Btn>}
            {contactPhone && <Btn onClick={() => setSmsOpen(true)}><FaSms /> SMS</Btn>}
            {call.lead_id && <AddToRouteButton leadId={call.lead_id} />}
            <Btn $danger onClick={() => setDeleteOpen(true)}><FaTrash /> Delete</Btn>
          </Actions>
        </HeroCard>

        <div style={{ height: 16 }} />

        <Grid>
          <Col>
            <Card>
              <h3><FaPhoneAlt /> Lead</h3>
              {(lead || call.lead) ? (
                <>
                  <InfoList>
                    <div className="row"><FaPhoneAlt /> {contactPhone ? formatPhone(contactPhone) : <span className="muted">No phone</span>}</div>
                    <div className="row"><FaEnvelope /> {contactEmail || <span className="muted">No email</span>}</div>
                    {primaryAddress && <div className="row"><FaMapMarkerAlt /> {primaryAddress}</div>}
                  </InfoList>

                  {lead && (
                    <>
                      <Stats>
                        <Stat><div className="num">{estimates.length}</div><div className="lbl"><FaFileInvoiceDollar /> Quotes</div></Stat>
                        <Stat><div className="num">{jobs.length}</div><div className="lbl"><FaBriefcase /> Jobs</div></Stat>
                      </Stats>
                      <OpenLeadBtn onClick={openFullLead}><FaExternalLinkAlt /> Open Full Lead</OpenLeadBtn>
                    </>
                  )}
                  {!lead && (
                    <Unlinked style={{ marginTop: 12 }}>
                      Showing contact details captured on the booking. The full lead record could not be loaded.
                    </Unlinked>
                  )}
                </>
              ) : (
                <Unlinked>
                  This booking isn’t linked to a lead in the CRM. Contact details captured at booking:
                  <InfoList style={{ marginTop: 12 }}>
                    <div className="row"><FaPhoneAlt /> {contactPhone ? formatPhone(contactPhone) : <span className="muted">No phone</span>}</div>
                    <div className="row"><FaEnvelope /> {contactEmail || <span className="muted">No email</span>}</div>
                  </InfoList>
                </Unlinked>
              )}
            </Card>

            {serviceRequests.length > 0 && (
              <Card>
                <h3><FaTools /> Service Requested</h3>
                {serviceRequests.map(sr => (
                  <ServiceItem key={sr.id}>
                    <div className="top">
                      <span className="svc">{sr.Service?.service_name || 'Service'}</span>
                      {sr.LeadStatus?.status_name && <LeadPill>{sr.LeadStatus.status_name}</LeadPill>}
                    </div>
                    {sr.job_information && <div className="info">{sr.job_information}</div>}
                    {sr.job_observation && (
                      <NotesBox><span className="k">Customer notes</span>{sr.job_observation}</NotesBox>
                    )}
                  </ServiceItem>
                ))}
              </Card>
            )}

            {call.lead_id && (
              <Card>
                <h3><FaPhoneAlt /> Communication History</h3>
                <CommunicationHistory leadId={call.lead_id} />
              </Card>
            )}
          </Col>

          <Col>
            <Card>
              <h3>Booking Details</h3>
              <MetaRow><span className="k">Status</span><span className="v"><StatusBadge status={call.status} /></span></MetaRow>
              <MetaRow><span className="k">Source</span><span className="v">{call.source === 'public' ? 'Website' : 'Added by team'}</span></MetaRow>
              <MetaRow>
                <span className="k">Reminder</span>
                <span className="v"><FaBell size={11} /> {call.reminder_status}{call.reminder_sent_at ? ` · ${formatEastern(call.reminder_sent_at)}` : ''}</span>
              </MetaRow>
              <MetaRow>
                <span className="k">Confirm. email</span>
                <span className="v">{call.confirmation_email_sent ? <><FaCheck color="#66BB6A" /> Sent</> : <><FaTimes color="#999" /> No</>}</span>
              </MetaRow>
              <MetaRow>
                <span className="k">Confirm. SMS</span>
                <span className="v">{call.confirmation_sms_id ? <><FaCheck color="#66BB6A" /> Sent</> : <><FaTimes color="#999" /> No</>}</span>
              </MetaRow>
              <MetaRow><span className="k">Booked</span><span className="v">{formatEastern(call.createdAt)}</span></MetaRow>
              {call.canceled_at && <MetaRow><span className="k">Canceled</span><span className="v">{formatEastern(call.canceled_at)}</span></MetaRow>}
            </Card>

            {call.notes && (
              <Card>
                <h3>Booking Notes</h3>
                <div style={{ color: '#ddd', whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '0.9rem' }}>{call.notes}</div>
              </Card>
            )}
          </Col>
        </Grid>
      </Page>

      <BookingModal
        isOpen={editOpen}
        mode="edit"
        booking={call}
        onClose={() => setEditOpen(false)}
        onSaved={(msg) => { notify(msg); load(); }}
      />

      {smsOpen && (
        <SmsModal
          isOpen={smsOpen}
          onClose={() => setSmsOpen(false)}
          lead={{ id: call.lead_id, name: contactName, phone: contactPhone }}
          userId={user?.id}
        />
      )}

      {callOpen && (
        <CallModal
          isOpen={callOpen}
          onClose={() => setCallOpen(false)}
          customerPhone={contactPhone}
          customerName={contactName}
          leadId={call.lead_id}
        />
      )}

      <Modal
        title="Delete Booking"
        isOpen={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
      >
        <p style={{ color: '#ccc', lineHeight: 1.6 }}>
          Permanently delete this booking for <strong style={{ color: '#fff' }}>{contactName}</strong> on{' '}
          <strong style={{ color: '#fff' }}>{formatEastern(call.slot_start)}</strong>? This cannot be undone.
        </p>
      </Modal>

      <ModalNotification
        isOpen={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback(prev => ({ ...prev, show: false }))}
      />
    </DashboardLayout>
  );
};

export default BookingDetailPage;
